/*****************************************************
Naam: FirstTimeSetup.jsx
Datum: 2024-11-24
Versie: 1.0.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
Eerste keer setup scherm voor nieuwe gebruikers.

Functionaliteiten:
- Kies nieuwe gebruikersnaam
- Kies nieuw wachtwoord (2x bevestiging)
- Password visibility toggle (oogje)
- Validatie op wachtwoord match
- Opslaan in aparte kolommen (dashboardUsername, dashboardPassword)

Input: onSetupComplete functie
Output: Dashboard credentials opgeslagen
******************************************************/

import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const FirstTimeSetup = ({ onSetupComplete }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validatie
    if (!username || username.length < 3) {
      setError('Gebruikersnaam moet minimaal 3 karakters lang zijn');
      return;
    }

    if (!password || password.length < 8) {
      setError('Wachtwoord moet minimaal 8 karakters lang zijn');
      return;
    }

    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/first-time-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          dashboardUsername: username, 
          dashboardPassword: password 
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Account succesvol aangemaakt!\n\nJe kunt nu inloggen met je nieuwe credentials.');
        onSetupComplete();
      } else {
        setError(result.message || 'Fout bij aanmaken account');
      }
    } catch (error) {
      setError('Fout bij aanmaken account. Probeer het opnieuw.');
      console.error('Setup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900">
      <div className="bg-white shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-4xl font-bold text-emerald-700 mb-2">SendWell</div>
          <div className="text-gray-600 text-sm mb-4">Dashboard Setup</div>
          <div className="bg-blue-50 border border-blue-200 p-4 text-left">
            <p className="text-sm text-blue-800 font-medium mb-2">👋 Welkom!</p>
            <p className="text-xs text-blue-700">
              Dit is de eerste keer dat je het dashboard opent. 
              Maak een gebruikersnaam en wachtwoord aan voor toekomstige logins.
            </p>
          </div>
        </div>

        {/* Setup Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nieuwe Gebruikersnaam
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="Kies een gebruikersnaam"
              required
              autoFocus
              minLength={3}
            />
            <p className="text-xs text-gray-500 mt-1">Minimaal 3 karakters</p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nieuw Wachtwoord
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                placeholder="Kies een wachtwoord"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimaal 8 karakters</p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bevestig Wachtwoord
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                placeholder="Herhaal wachtwoord"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              ⚠️ {error}
            </div>
          )}

          {/* Password Strength Indicator */}
          {password.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">Wachtwoord Sterkte:</p>
              <div className="space-y-1 text-xs">
                <div className={password.length >= 8 ? 'text-green-600' : 'text-gray-400'}>
                  {password.length >= 8 ? '✓' : '○'} Minimaal 8 karakters
                </div>
                <div className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                  {/[A-Z]/.test(password) ? '✓' : '○'} Minimaal 1 hoofdletter
                </div>
                <div className={/[0-9]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                  {/[0-9]/.test(password) ? '✓' : '○'} Minimaal 1 cijfer
                </div>
                <div className={password === confirmPassword && password.length > 0 ? 'text-green-600' : 'text-gray-400'}>
                  {password === confirmPassword && password.length > 0 ? '✓' : '○'} Wachtwoorden komen overeen
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 font-semibold text-white transition-all transform hover:scale-105 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Account aanmaken...
              </span>
            ) : (
              '✓ Account Aanmaken'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Deze gegevens worden veilig opgeslagen in je lokale database.</p>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeSetup;
